import {
  Box,
  Breadcrumbs,
  Divider,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  SectionHeader,
  SectionNavigation,
  SectionNavigationItem,
  useDialogService,
  Typography,
} from "@platform/design-system";
import { useRouteMatch } from "@platform/hooks";
import { Link, Outlet, useLocation, useParams } from "react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { useStorage } from "@platform/hooks";
import {
  BBE_MONTHLY_PROGRESS,
  BBE_SCENARIO_MODELING,
} from "@platform/feature-flags";
import NotesIcon from "@mui/icons-material/Notes";

import { ROUTE_NAMES } from "../../constants";
import { useBrokerHeaderInfo } from "../../hooks/useBbe";
import { CircularProgressCentered } from "../../components/CircularProgressCentered";

import { HeaderExtra } from "./components/HeaderExtras/HeaderExtra";
import {
  BBEBroker as BBEBrokerType,
  BBEOutletContext,
  BrokerBonusSummary,
} from "./types";

const bonusForecastLabel = "Bonus Forecast";

const BBE_SELECTED_MODEL_STORAGE_KEY = "bbe-selectedModelId";

export const BBEBroker = (): React.JSX.Element => {
  const location = useLocation();
  const monthlyProgressEnabled = useFeatureIsOn(BBE_MONTHLY_PROGRESS);
  const scenarioModelingEnabled = useFeatureIsOn(BBE_SCENARIO_MODELING);

  const { id: brokerIdParam, scenarioId = "" } = useParams<{
    id: string;
    scenarioId?: string;
  }>();
  const brokerId = brokerIdParam ?? "";

  const { getStorageItem, setStorageItem, removeStorageItem } =
    useStorage("local");

  const [selectedModelId, setSelectedModelId] = useState<string>(() => {
    const stored = getStorageItem<{ brokerId: string; modelId: string }>(
      BBE_SELECTED_MODEL_STORAGE_KEY,
    );
    return stored?.brokerId === brokerId ? stored.modelId : "";
  });

  const prevBrokerIdRef = useRef(brokerId);

  useEffect(() => {
    if (prevBrokerIdRef.current !== brokerId) {
      prevBrokerIdRef.current = brokerId;
      setSelectedModelId("");
      return;
    }
    if (selectedModelId) {
      setStorageItem(BBE_SELECTED_MODEL_STORAGE_KEY, {
        brokerId,
        modelId: selectedModelId,
      });
    } else {
      removeStorageItem(BBE_SELECTED_MODEL_STORAGE_KEY);
    }
  }, [selectedModelId, brokerId, setStorageItem, removeStorageItem]);

  const theme = useTheme();

  const { openDialog } = useDialogService();

  const { navItems, routePaths } = getNavItems(
    monthlyProgressEnabled,
    scenarioModelingEnabled,
  );

  const route = useRouteMatch(routePaths, {
    baseRoute: `/${ROUTE_NAMES.BBE}/:id/`,
    defaultValue: ROUTE_NAMES.BBE_FORECAST,
  });

  const activeTabLabel: { label: string; link?: string }[] = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);

    const isForecastRoute = location.pathname.includes(
      ROUTE_NAMES.BBE_FORECAST,
    );

    if (isForecastRoute || route === "forecast") {
      const basePath = `${location.pathname.split("/forecast")[0]}/forecast`;

      const breadcrumbs: { label: string; link?: string }[] = [
        {
          label: bonusForecastLabel,
          link: basePath,
        },
      ];

      if (pathSegments[3] === ROUTE_NAMES.BBE_BROKER_SETTINGS) {
        breadcrumbs.push({
          label: "Carrier Bonus Settings",
        });
      }

      if (pathSegments[3] === ROUTE_NAMES.BBE_CARRIER_BONUS_DETAILS) {
        breadcrumbs.push({
          label: "Carrier Bonus Details",
        });
      }

      return breadcrumbs;
    }

    const isScenarioDetailRoute =
      location.pathname.includes(ROUTE_NAMES.BBE_SCENARIO_MODELING) &&
      pathSegments.length > 3;

    if (isScenarioDetailRoute) {
      const basePath = `${location.pathname.split(`/${ROUTE_NAMES.BBE_SCENARIO_MODELING}`)[0]}/${ROUTE_NAMES.BBE_SCENARIO_MODELING}`;
      return [
        { label: "Scenario Modeling", link: basePath },
        { label: "Scenario Details" },
      ];
    }

    const matchedNav = navItems.find((item) => item.value === route);

    if (matchedNav?.label) {
      return [{ label: matchedNav.label }];
    }

    return [{ label: bonusForecastLabel }];
  }, [location.pathname, navItems, route]);

  const axiosInstance = useApi();

  const brokersQuery = useQuery<BBEBrokerType[]>({
    queryKey: ["bbeBrokers"],
    queryFn: () =>
      axiosInstance
        .get<BBEBrokerType[]>("/bbe/v1/brokers")
        .then((res) => res.data),
  });

  const selectedBroker = useMemo(
    () =>
      brokersQuery.data?.find(
        (broker: BBEBrokerType) => broker.salesforceId === brokerId,
      ),
    [brokerId, brokersQuery.data],
  );

  const brokerBonusQuery = useQuery<BrokerBonusSummary>({
    queryKey: ["bonusSummary", brokerId],
    queryFn: () =>
      axiosInstance
        .get<BrokerBonusSummary>(`/bbe/v1/broker/${brokerId}/bonus-summary`)
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });

  const { data, loading } = useBrokerHeaderInfo(brokerId);
  const handleScenarioModelingNotesDrawer = useCallback(() => {
    openDialog({
      type: "bbeScenarioNotes",
      data: { modelId: selectedModelId, scenarioId },
      width: "40%",
      disableExternalClose: true,
    });
  }, [openDialog, scenarioId, selectedModelId]);

  if (brokersQuery.isLoading || loading) {
    return <CircularProgressCentered />;
  }

  if (brokersQuery.isError || !selectedBroker) {
    return (
      <Typography color="danger">Unable to load broker information.</Typography>
    );
  }

  const outletContext: BBEOutletContext = {
    brokerId,
    brokerName: selectedBroker.name,
    bonusSummary: brokerBonusQuery.data,
    selectedModelId,
    setSelectedModelId,
  };

  return (
    <Box mt={8} flex={1} display="flex" flexDirection="column">
      <SectionHeader
        title={`Broker: ${selectedBroker.name}`}
        extra={
          <HeaderExtra
            data={data}
            brokerId={brokerId}
            route={route}
            selectedModelId={selectedModelId}
          />
        }
        breadcrumbs={
          <Breadcrumbs sx={{ color: theme.palette.common.white }}>
            <Link
              to={`/${ROUTE_NAMES.BBE}`}
              style={{ color: theme.palette.common.white }}
            >
              BBE
            </Link>
            {activeTabLabel.map((tab) =>
              tab?.link ? (
                <Link
                  key={tab.label}
                  to={tab.link}
                  style={{ color: theme.palette.common.white }}
                >
                  {tab.label}
                </Link>
              ) : (
                <Typography key="label" variant="body-large" color="inherit">
                  {tab.label}
                </Typography>
              ),
            )}
          </Breadcrumbs>
        }
      />

      <Stack spacing={0} flex={1}>
        <SectionNavigation
          navItems={navItems}
          value={route}
          slotProps={{
            container: {
              sx: {
                px: 3,
                display: "flex",
                overflowX: "auto",
                alignItems: "center",
                columnGap: 2,
              },
            },
            tabs: {
              textColor: "secondary",
              indicatorColor: "secondary",
              sx: {
                "& .MuiTabs-indicator": {
                  backgroundColor: "brandPrimary.main",
                },
                "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
              },
            },
          }}
          extra={
            route === ROUTE_NAMES.BBE_SCENARIO_MODELING ? (
              <IconButton onClick={handleScenarioModelingNotesDrawer}>
                <NotesIcon />
              </IconButton>
            ) : null
          }
        />
        <Divider />

        <Box width="100%" px={"20px"} flex={1} display="flex">
          <Stack p={3} flex={1}>
            <Outlet context={outletContext} />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

type RouteKey =
  | typeof ROUTE_NAMES.BBE_MONTHLY_PROGRESS
  | typeof ROUTE_NAMES.BBE_FORECAST
  | typeof ROUTE_NAMES.BBE_SCENARIO_MODELING
  | typeof ROUTE_NAMES.BBE_BROKER_SETTINGS;

const getNavItems = (
  monthlyProgressEnabled: boolean,
  scenarioModelingEnabled: boolean,
): {
  navItems: SectionNavigationItem<RouteKey>[];
  routePaths: [RouteKey, string][];
} => {
  const navItems: SectionNavigationItem<RouteKey>[] = [];

  if (monthlyProgressEnabled) {
    navItems.push({
      label: "Monthly Progress",
      path: ROUTE_NAMES.BBE_MONTHLY_PROGRESS,
      value: ROUTE_NAMES.BBE_MONTHLY_PROGRESS,
    });
  }

  navItems.push({
    label: bonusForecastLabel,
    path: ROUTE_NAMES.BBE_FORECAST,
    value: ROUTE_NAMES.BBE_FORECAST,
  });

  if (scenarioModelingEnabled) {
    navItems.push({
      label: "Scenario Modeling",
      path: ROUTE_NAMES.BBE_SCENARIO_MODELING,
      value: ROUTE_NAMES.BBE_SCENARIO_MODELING,
    });
  }

  const routePaths = navItems.flatMap<[RouteKey, string]>((item) => [
    [item.value, item.path],
    [item.value, `${item.path}/*`],
  ]);

  return { navItems, routePaths };
};

export default BBEBroker;
